import {
  useRef,
  useEffect,
  useCallback,
  useState,
  useLayoutEffect,
} from "react";
import { Container } from "pixi.js";
import {
  VirtualListManager,
  VirtualListOptions,
  VirtualItem,
} from "../lib/VirtualListManager";

export type { VirtualItem };

export interface UseVirtualListOptions extends VirtualListOptions {}

export function useVirtualList({
  itemCount,
  itemSize,
  overscan = 3,
  initialIndex = 0,
  direction = "vertical",
  viewportSize,
}: UseVirtualListOptions) {
  // Store the manager instance in a ref to avoid recreating it
  const managerRef = useRef<VirtualListManager | null>(null);

  // Only re-render when virtualItems change
  const [virtualItems, setVirtualItems] = useState<VirtualItem[]>([]);

  // Refs for container and scroll - these don't trigger re-renders
  const containerRef = useRef<Container | null>(null);
  const scrollRef = useRef<Container | null>(null);

  // Initialize manager only once
  useEffect(() => {
    if (!managerRef.current) {
      managerRef.current = new VirtualListManager({
        itemCount,
        itemSize,
        overscan,
        initialIndex,
        direction,
        viewportSize,
      });

      // Subscribe to virtualItems changes
      managerRef.current.subscribe((items) => {
        setVirtualItems(items);
      });

      // Set initial virtual items
      setVirtualItems(managerRef.current.getVirtualItems());
    }

    return () => {
      managerRef.current?.destroy();
      managerRef.current = null;
    };
  }, []); // Only run once on mount

  // Update manager when options change
  useEffect(() => {
    if (managerRef.current) {
      managerRef.current.updateItemCount(itemCount);
    }
  }, [itemCount]);

  useEffect(() => {
    if (managerRef.current) {
      managerRef.current.updateItemSize(itemSize);
    }
  }, [itemSize]);

  useEffect(() => {
    if (managerRef.current) {
      managerRef.current.updateViewportSize(viewportSize);
    }
  }, [viewportSize]);

  // Sync refs with manager using layout effect to ensure refs are set before paint
  useLayoutEffect(() => {
    if (managerRef.current) {
      if (containerRef.current) {
        managerRef.current.setContainerRef(containerRef.current);
      }
      if (scrollRef.current) {
        managerRef.current.setScrollRef(scrollRef.current);
      }
    }
  }); // Run after every render to sync refs

  // Memoized callbacks that use the manager
  const updateScroll = useCallback((newOffset: number) => {
    managerRef.current?.updateScroll(newOffset);
  }, []);

  const scrollToIndex = useCallback((index: number) => {
    managerRef.current?.scrollToIndex(index);
  }, []);

  // Get values directly from manager without causing re-renders
  // These are computed on every render, but don't trigger re-renders themselves
  const totalSize = managerRef.current?.getTotalSize() ?? 0;
  const itemSizes = managerRef.current?.getItemSizes() ?? [];
  const itemPositions = managerRef.current?.getItemPositions() ?? [];

  return {
    virtualItems,
    totalSize,
    scrollToIndex,
    containerRef,
    scrollRef,
    direction,
    updateScroll,
    itemSizes,
    itemPositions,
  };
}
